/**
 * OÙ LE LOT DE RÉACTIF A CHANGÉ — module PUR.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── La demande (RQ, 11/09/2026) ────────────────────────────────────────────
 *
 * « La carte de CQI change totalement quand on a un changement de lot du
 *  contrôle. Par contre, au moment du changement du lot de réactif, on fait
 *  juste un nouveau passage de CQI et on continue sur la même carte. Je
 *  voudrais que tu affiches le début d'un nouveau lot de réactifs par un trait
 *  vertical sur la courbe. »
 *
 * Deux registres, et c'est tout l'objet de ce module. Le lot de CONTRÔLE ouvre
 * une carte neuve : cible et écart-type changent, les points d'avant et d'après
 * ne se comparent plus. Le lot de RÉACTIF se marque SUR la carte en cours :
 * c'est une cause possible qu'on note, pas une rupture de référentiel.
 *
 * ── Pourquoi un module, pour six lignes ────────────────────────────────────
 *
 * Parce que les six lignes portent trois décisions qu'on ne redevine pas, et
 * qu'elles vivaient dans le JSX du graphe — donc hors de portée du lanceur, qui
 * n'éprouve que les modules purs. La première version portait en plus un `let`
 * réassigné pendant le rendu, que le compilateur React refuse.
 */

/** Le strict nécessaire : ce que le graphe connaît déjà de chaque point. */
export interface PointLot {
  date: string;
  lotReactif?: string | null;
}

/** Un trait à tracer : l'index du point, le lot qui commence, et depuis quand. */
export interface ChangementLot {
  i: number;
  lot: string;
  date: string;
}

/**
 * Les endroits où le lot de réactif CHANGE, dans l'ordre de la série.
 *
 * ⚠️ TROIS RÈGLES, et chacune corrige une façon de se tromper :
 *
 * ① **On ne marque que les CHANGEMENTS.** Un trait sur le premier point dirait
 *    « ici commence un nouveau lot » alors qu'on ne sait rien d'avant — la
 *    série affichée est une fenêtre, pas le début du monde.
 *
 * ② **Un passage SANS lot n'interrompt rien.** La colonne est vide sur la
 *    plupart des passages repris (7 654 sur 8 101 au 12/09/2026). Traiter
 *    `null` comme une valeur ferait DEUX traits à chaque trou — « fin du lot »
 *    puis « retour du lot » — pour une information qu'on n'a pas.
 *
 * ③ **Un retour à un lot déjà vu est un changement.** On compare au lot
 *    PRÉCÉDENT, pas à l'ensemble des lots rencontrés : reprendre un ancien
 *    flacon est un événement, et c'est même celui qu'on voudra retrouver si la
 *    série décroche.
 *
 * Les valeurs sont comparées après `trim` : « AS09 » et « AS09 » séparés par
 * une espace de saisie ne sont pas deux lots.
 */
export function changementsDeLot(points: PointLot[]): ChangementLot[] {
  const out: ChangementLot[] = [];
  let precedent = '';
  points.forEach((p, i) => {
    const lot = String(p.lotReactif ?? '').trim();
    if (!lot) return;
    if (precedent && lot !== precedent) out.push({ i, lot, date: p.date });
    precedent = lot;
  });
  return out;
}
